import React from 'react';
import { theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

// The tooltip copy lives in a data attribute react-tooltip reads, which
// assistive tech cannot reach — hence the duplicate below for screen readers.
const SR_ONLY: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export type KpiStatLabelProps = {
  id: string;
  label: string;
  info: string;
  /** Decorative: every stat states its own number and share in text. */
  accent?: string;
};

/**
 * Wraps rather than truncates — nothing here may be clipped, so the strip grows
 * instead. Two label lines are reserved whether or not the label needs them, so
 * the big numbers sit on one line across the row: `controlHeightLG` is exactly
 * two `lineHeightSM` lines at `fontSizeSM`.
 */
function KpiStatLabel({ id, label, info, accent }: KpiStatLabelProps) {
  const { token } = theme.useToken();

  return (
    <>
      <CustomTooltip
        placement="top"
        message={info}
        wrapperStyle={{
          display: 'inline-flex',
          // Without this the parent `Flex vertical` (align-items: stretch by
          // default) stretches this anchor to the full tile width, so the
          // top-placed tooltip centres over empty space instead of the label.
          alignSelf: 'flex-start',
          alignItems: 'flex-start',
          gap: token.marginXXS,
          maxWidth: '100%',
          minHeight: token.controlHeightLG,
          cursor: 'help',
          fontSize: token.fontSizeSM,
          fontWeight: token.fontWeightStrong,
          lineHeight: token.lineHeightSM,
          color: token.colorTextSecondary,
          textAlign: 'left',
        }}
      >
        {accent && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: token.fontSizeSM * token.lineHeightSM,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: token.sizeXS,
                height: token.sizeXS,
                borderRadius: '50%',
                background: accent,
              }}
            />
          </span>
        )}
        <span>{label}</span>
        <InfoCircleOutlined
          aria-hidden="true"
          style={{ fontSize: token.fontSizeSM, flexShrink: 0, marginTop: token.marginXXS }}
        />
      </CustomTooltip>
      <span id={`${id}-info`} style={SR_ONLY}>
        {info}
      </span>
    </>
  );
}

export default KpiStatLabel;
